/* ===== File Upload & Compression ===== */
import { storage } from '../config/firebase.js?v=82.86';
import { showUploadProgress } from '../utils/ui.js?v=82.86';
import { logger } from '../utils/logger.js?v=82.86';
import { withTimeoutMs } from '../utils/with-timeout.js?v=82.86';

const FOLDER_PROFILES = {
  backgrounds:     { maxWidth: 1920, quality: 0.75 },
  prizes:          { maxWidth: 800,  quality: 0.80 },
  prizes_default:  { maxWidth: 800,  quality: 0.80 },
  'client-promo':  { maxWidth: 1200, quality: 0.75 },
  confirm_bg:      { maxWidth: 1200, quality: 0.75 },
  qr:              { maxWidth: 600,  quality: 0.82 },
  'menu-qr':       { maxWidth: 600,  quality: 0.82 },
  'menu-gallery':  { maxWidth: 1600, quality: 0.78 },
  'feedback-qr':   { maxWidth: 600,  quality: 0.82 },
  'location-hours': { maxWidth: 1600, quality: 0.78 },
  'location-logo':  { maxWidth: 800,  quality: 0.85 },
  'event-gallery':  { maxWidth: 1200, quality: 0.75 },
  'loyalty-promo':  { maxWidth: 1200, quality: 0.75 },
  'joy-assistant':  { maxWidth: 1600, quality: 0.78 },
};
const DEFAULT_PROFILE = { maxWidth: 1280, quality: 0.78 };
/** Limită pe blob-ul comprimat urcat în Storage. */
export const MAX_COMPRESSED_UPLOAD_BYTES = 5 * 1024 * 1024;
/** Dacă compresia eșuează, acceptăm original doar până la această mărime. */
export const MAX_ORIGINAL_FALLBACK_BYTES = 40 * 1024 * 1024;
const MAX_SIZE = MAX_COMPRESSED_UPLOAD_BYTES;
const MAX_ORIGINAL_FALLBACK = MAX_ORIGINAL_FALLBACK_BYTES;

function isCoarsePointerDevice() {
  try {
    return (
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(pointer: coarse)').matches
    );
  } catch (_) {
    return false;
  }
}
const COMPRESS_TIMEOUT_MS = 30000;
const UPLOAD_TIMEOUT_MS = 90000;

export function updateUploadProgress(pct, text) {
  showUploadProgress(pct, text);
}

function supportsWebP() {
  try {
    const c = document.createElement('canvas');
    c.width = 1;
    c.height = 1;
    return c.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  } catch (_) {
    return false;
  }
}

function loadImageFromObjectUrl(objectUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(err || new Error('Nu s-a putut încărca imaginea.'));
    img.src = objectUrl;
  });
}

function canvasToBlob(canvas, mime, quality) {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error('Compresia imaginii a durat prea mult.'));
    }, COMPRESS_TIMEOUT_MS);

    canvas.toBlob(
      (blob) => {
        window.clearTimeout(timer);
        if (blob) resolve(blob);
        else reject(new Error('Compresia imaginii a eșuat.'));
      },
      mime,
      quality
    );
  });
}

async function compressImage(file, folder) {
  const profile = FOLDER_PROFILES[folder] || DEFAULT_PROFILE;
  const maxW = isCoarsePointerDevice()
    ? Math.min(profile.maxWidth, 1400)
    : profile.maxWidth;
  const q = profile.quality;
  const objectUrl = URL.createObjectURL(file);

  try {
    const img = await withTimeoutMs(loadImageFromObjectUrl(objectUrl), COMPRESS_TIMEOUT_MS);

    const canvas = document.createElement('canvas');
    let width = img.naturalWidth || img.width;
    let height = img.naturalHeight || img.height;

    if (!width || !height) {
      throw new Error('Dimensiunile imaginii nu pot fi citite.');
    }

    if (width > maxW) {
      height *= maxW / width;
      width = maxW;
    }

    canvas.width = Math.round(width);
    canvas.height = Math.round(height);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas indisponibil pe acest dispozitiv.');
    }
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const useWebP = supportsWebP();
    const mime = useWebP ? 'image/webp' : 'image/jpeg';
    const ext = useWebP ? '.webp' : '.jpg';
    const blob = await canvasToBlob(canvas, mime, q);
    return { blob, ext, mime };
  } finally {
    try {
      URL.revokeObjectURL(objectUrl);
    } catch (_) {
      /* ignore */
    }
  }
}

export async function uploadFile(uid, folder, file) {
  if (!file) return null;

  console.log('IMAGE SELECTED', file.name, file.size, file.type);

  let fileToUpload = file;

  const looksLikeImage =
    /^image\//.test(file.type || '') ||
    /\.(jpe?g|png|gif|webp|heic|heif)$/i.test(file.name || '') ||
    (!file.type && file.size > 0);

  if (looksLikeImage) {
    try {
      const { blob, ext, mime } = await compressImage(file, folder);
      const baseName = (file.name || 'image').replace(/\.[^/.]+$/, '');
      fileToUpload = new File([blob], baseName + ext, { type: mime });
    } catch (e) {
      logger.warn('Compression failed.', e);
      if (file.size > MAX_ORIGINAL_FALLBACK) {
        const maxMb = (MAX_ORIGINAL_FALLBACK / (1024 * 1024)).toFixed(0);
        throw new Error(
          `Imaginea este prea mare (max ${maxMb}MB). Încearcă o poză mai mică sau alt format (JPG).`
        );
      }
      console.warn('UPLOAD FALLBACK original (compression failed)', e?.message);
    }
  }

  if (fileToUpload.size > MAX_SIZE) {
    const maxMb = (MAX_SIZE / (1024 * 1024)).toFixed(0);
    throw new Error(`Imaginea comprimată este încă prea mare (max ${maxMb}MB). Încearcă altă poză.`);
  }

  const originalName = fileToUpload.name || 'img.jpg';
  const safeName = originalName.replace(/[^\w.\-]+/g, '_');
  const path = `${uid}/${folder}/${Date.now()}_${safeName}`;

  const ref = storage.ref(path);
  const contentType =
    /^image\/(jpeg|jpg|png|gif|webp|avif|heic|heif)$/.test(String(fileToUpload.type || ''))
      ? fileToUpload.type
      : safeName.match(/\.webp$/i)
        ? 'image/webp'
        : safeName.match(/\.png$/i)
          ? 'image/png'
          : 'image/jpeg';

  console.log('UPLOAD START', path, fileToUpload.size);
  const task = ref.put(fileToUpload, { contentType });

  await withTimeoutMs(
    new Promise((res, rej) => {
      task.on(
        'state_changed',
        (s) => {
          const pct = (s.bytesTransferred / Math.max(s.totalBytes, 1)) * 100;
          updateUploadProgress(pct);
        },
        rej,
        res
      );
    }),
    UPLOAD_TIMEOUT_MS
  );

  const url = await withTimeoutMs(ref.getDownloadURL(), 15000);
  console.log('UPLOAD DONE', url);
  updateUploadProgress(100, 'Gata.');
  return url;
}
